// The browse catalogue (topics, shows, a compact episode list, Top 10, the Plus plan).
// Mock mode: the sample data. Live mode: GET /v1/catalog, kept in localStorage so the app paints
// instantly on the next start and works on a weak connection, then refreshed in the background.
import { useSyncExternalStore } from 'react';
import { EPISODES, PLAN, SHOWS, TOP10_IDS, TOPICS } from './data';
import { load, save } from './storage';
import { rememberShows } from './showIndex';
import type { Episode, Plan, Show, Topic } from './types';

declare const __API_MODE__: 'mock' | 'http';
declare const __API_BASE__: string;

export interface Catalog { topics: Topic[]; shows: Show[]; episodes: Episode[]; top10: string[]; plan: Plan; version: number; ready: boolean }

const KEY = 'kn-catalog-v1';
const MOCK: Catalog = { topics: TOPICS, shows: SHOWS, episodes: EPISODES, top10: TOP10_IDS, plan: PLAN, version: 0, ready: true };
const EMPTY: Catalog = { topics: [], shows: [], episodes: [], top10: [], plan: PLAN, version: 0, ready: false };

let cat: Catalog = __API_MODE__ === 'http' ? { ...EMPTY, ...load<Partial<Catalog>>(KEY, {}) } : MOCK;
if (__API_MODE__ === 'http' && cat.shows.length) { cat.ready = true; rememberShows(cat.shows); }
const listeners = new Set<() => void>();

export function getCatalog(): Catalog { return cat; }

let inflight: Promise<void> | null = null;
/** Fetch the latest catalogue (live mode). Safe to call often; requests are shared. */
export function refreshCatalog(): Promise<void> {
  if (__API_MODE__ !== 'http') return Promise.resolve();
  inflight ||= fetch(__API_BASE__ + '/catalog', { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
    .then((next: Omit<Catalog, 'ready'>) => {
      cat = { ...next, plan: next.plan || PLAN, ready: true };
      rememberShows(cat.shows);
      save(KEY, { ...cat, ready: undefined });
      listeners.forEach((l) => l());
    })
    .catch(() => { if (!cat.ready) { cat = { ...cat, ready: true }; listeners.forEach((l) => l()); } })
    .finally(() => { inflight = null; });
  return inflight;
}

export function useCatalog(): Catalog {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => cat, () => cat);
}

export const topicName = (c: Catalog, slug: string) => c.topics.find((t) => t.slug === slug)?.name || '';
